// COO (Sentinel's agent) on NomOS's Daimon (SEN-395 / TZ-2). Reshape only.
//
// Ported:
//  - runs, run detail, stop      -> /daimon/runs, /daimon/runs/{id}, /daimon/runs/{id}/stop (agent_invocations; admin,
//                                   tenant-wide, metadata only). A run is a chat turn (job 'chat') or a direct invoke.
//  - status, pause, resume       -> /daimon/status, /daimon/pause, /daimon/resume (daimon_controls).
//  - egress denials              -> /sandbox/egress-denials: host names the SANDBOX RUNNER's egress proxy refused.
//                                   Lossy: Sentinel lists refusals of the COO agent host (OpenClaw behind squid); the
//                                   Daimon calls its model from the API process and has no egress confinement of its
//                                   own, so the only refused egress NomOS can report is the runner's (KMD login today).
//  - facts                       -> /daimon/facts (daimon_facts). Building ids <-> site record ids via
//                                   sentinel_building_id; scope wording reshaped to Sentinel's ("Customer-wide").
// Gaps (NomosGapError, see the constants below): COO automations (8 methods) and chat files (2 methods).
import { AxiosError } from 'axios';
import type { CooEgressDenial, CooRun, CooRunDetail, CooStatus, CooStopResult, CooToolCall } from '@/types';
import { nomosFetch, toAxiosError, zulu, type NomosEnvelope } from '../client';
import type { AnyFn, NomosRegistry } from '../registry';
import { NomosGapError } from './fieldops';

// ---------- gaps ----------

export const AUTOMATIONS_GAP =
  "COO automations: NomOS cannot run them. A Sentinel automation is a scheduled agent run WITH tools whose result is " +
  "delivered to recipients' chats. The Daimon calls its tools only with the initiating person's own bearer " +
  "(AgentRuntime forwards it and refuses to mint one), so an unattended scheduled Daimon run has no authority in " +
  "production; the automation rail's agent_task is a different, tool-less judgement over records; the rail has no " +
  "cron cadence (hourly/daily only) and Daimon threads have no automation delivery. Saving automations that can " +
  "never run would be fake. Needs: an unattended authority for the rail (e.g. approval mints the approver's api_key, " +
  "held as the automation's secret_ref), a daimon_result action kind, a cron due-hook in the scheduler, and " +
  "daimon_threads.automation_id for delivery.";

const gap = (msg: string) => async (): Promise<never> => { throw new NomosGapError(msg); };

// ---------- errors: NomOS refusal -> Sentinel {detail, code} with Sentinel's status ----------

type CodeMap = Record<string, { code: string; status: number }>;

function sentinelError(status: number, code: string, detail: string): AxiosError {
  const e = toAxiosError(status, { detail }, '');
  (e.response!.data as Record<string, unknown>).code = code;
  return e;
}

async function coded<T>(p: Promise<T>, map: CodeMap = {}): Promise<T> {
  try {
    return await p;
  } catch (e) {
    const data = (e as AxiosError).response?.data as { detail?: string; error?: { details?: { code?: string } } } | undefined;
    const nomosCode = data?.error?.details?.code;
    if (!nomosCode || !(e as AxiosError).response) throw e;
    const m = map[nomosCode] ?? { code: nomosCode, status: (e as AxiosError).response!.status };
    throw sentinelError(m.status, m.code, data?.detail ?? nomosCode);
  }
}

// ---------- runs ----------

interface NRun extends Omit<CooRun, 'kind'> { kind: string }
interface NRunDetail extends Omit<CooRunDetail, 'kind' | 'tool_calls'> {
  kind: string; tool_calls: Array<Omit<CooToolCall, 'started_at'> & { started_at: string | null }>;
}

/** NomOS error codes renamed to the words Sentinel's UI already labels (lib/coo-tools). */
const ERROR_CODES: Record<string, string> = { daimon_paused: 'coo_paused' };
const errorCode = (c: string | null) => (c === null ? null : ERROR_CODES[c] ?? c);

function runRow(r: NRun): CooRun {
  return {
    run_id: r.run_id, kind: 'coo', source: r.source, customer_id: r.customer_id, job: r.job, status: r.status,
    trigger_source: r.trigger_source, started_at: zulu(r.started_at), completed_at: zulu(r.completed_at),
    tool_calls: r.tool_calls, failed_tool_calls: r.failed_tool_calls, error_code: errorCode(r.error_code),
    acting_user_name: r.acting_user_name, stop_requested_at: zulu(r.stop_requested_at), stopped_by_name: r.stopped_by_name,
  };
}

async function getCooRuns(params?: { status?: string; job?: string }): Promise<CooRun[]> {
  const query: Record<string, string> = {};
  if (params?.status) query.status = params.status;
  if (params?.job) query.job = params.job;
  const r = await nomosFetch<NomosEnvelope<NRun[]>>('GET', '/daimon/runs', undefined,
    Object.keys(query).length ? { query } : {});
  return (r.data ?? []).map(runRow);
}

async function getCooRun(runId: string): Promise<CooRunDetail> {
  const d = (await coded(nomosFetch<NomosEnvelope<NRunDetail>>('GET', `/daimon/runs/${encodeURIComponent(runId)}`))).data;
  const details = (d.details ?? {}) as Record<string, unknown>;
  const errors: Record<string, { count: number }> = {};
  for (const [k, v] of Object.entries(d.errors_by_class ?? {})) errors[errorCode(k)!] = v;
  return {
    run_id: d.run_id, kind: 'coo', source: d.source, customer_id: d.customer_id, job: d.job, status: d.status,
    trigger_source: d.trigger_source, started_at: zulu(d.started_at), completed_at: zulu(d.completed_at),
    failed_tool_calls: d.failed_tool_calls, error_code: errorCode(d.error_code),
    stop_requested_at: zulu(d.stop_requested_at), stopped_by_name: d.stopped_by_name,
    trigger_reasoning: d.trigger_reasoning,
    // the run sheet reads details.model and details.usage (as Sentinel's runner records them)
    details: { ...details, usage: { input_tokens: details.input_tokens ?? null, output_tokens: details.output_tokens ?? null } },
    errors_by_class: errors,
    tool_calls: (d.tool_calls ?? []).map((c) => ({
      seq: c.seq, started_at: zulu(c.started_at ?? d.started_at), tool: c.tool, method: c.method, path: c.path,
      status_code: c.status_code, ok: c.ok, duration_ms: c.duration_ms, error_code: c.error_code,
    })),
  };
}

async function stopCooRun(runId: string): Promise<CooStopResult> {
  const d = (await coded(nomosFetch<NomosEnvelope<CooStopResult>>('POST', `/daimon/runs/${encodeURIComponent(runId)}/stop`))).data;
  return { run_id: d.run_id, stop_requested_at: zulu(d.stop_requested_at) };
}

async function getCooEgressDenials(): Promise<CooEgressDenial[]> {
  const r = await nomosFetch<NomosEnvelope<Array<CooEgressDenial & { profile?: string }>>>('GET', '/sandbox/egress-denials');
  return (r.data ?? []).map((x) => ({
    id: x.id, run_id: x.run_id, occurred_at: zulu(x.occurred_at), host: x.host, port: x.port, reason: x.reason,
  }));
}

// ---------- status / pause ----------

const status = (d: CooStatus): CooStatus => ({ paused: !!d.paused, paused_at: zulu(d.paused_at), paused_by_name: d.paused_by_name });
const getCooStatus = async (): Promise<CooStatus> => status((await nomosFetch<NomosEnvelope<CooStatus>>('GET', '/daimon/status')).data);
const pauseCoo = async (): Promise<CooStatus> => status((await nomosFetch<NomosEnvelope<CooStatus>>('POST', '/daimon/pause')).data);
const resumeCoo = async (): Promise<CooStatus> => status((await coded(nomosFetch<NomosEnvelope<CooStatus>>('POST', '/daimon/resume'))).data);

// ---------- facts ----------

// ---------- registry ----------

const automationGap = gap(AUTOMATIONS_GAP);
export const cooImpl: NomosRegistry = {
  getCooRuns: getCooRuns as unknown as AnyFn,
  getCooRun: getCooRun as unknown as AnyFn,
  getCooEgressDenials: getCooEgressDenials as unknown as AnyFn,
  stopCooRun: stopCooRun as unknown as AnyFn,
  getCooStatus: getCooStatus as unknown as AnyFn,
  pauseCoo: pauseCoo as unknown as AnyFn,
  resumeCoo: resumeCoo as unknown as AnyFn,
  listCooAutomations: automationGap as unknown as AnyFn,
  getCooAutomation: automationGap as unknown as AnyFn,
  getCooAutomationOptions: automationGap as unknown as AnyFn,
  createCooAutomation: automationGap as unknown as AnyFn,
  updateCooAutomation: automationGap as unknown as AnyFn,
  approveCooAutomation: automationGap as unknown as AnyFn,
  deleteCooAutomation: automationGap as unknown as AnyFn,
};
